import { PAYMENTS_SERVICE_URI } from "env";
import { assertOk, proxyFetch } from "../utils";
export { default as useDeletePaymentMethod } from "./useDeletePaymentMethod";
export { default as useDeleteBankAccount } from "./useDeleteBankAccount";
export { default as useUpdatePaymentMethod } from "./useUpdatePaymentMethod";
export { default as useUpdateBankAccount } from "./useUpdateBankAccount";
export { default as useGetBankRailOptions } from "./useGetBankRailOptions";
export { default as useCreateBankingPaymentMethod } from "./useCreateBankingPaymentMethod";
export { default as useGetIbanOptions } from "./useGetIbanOptions";
export { default as useCreateIbanPaymentMethod } from "./useCreateIbanPaymentMethod";

const paymentsProxy = proxyFetch(PAYMENTS_SERVICE_URI);

export const updatePaymentMethod = async (ctx, id, body) => {
  if (!id) {
    throw new Error("Payment method ID is missing when attempting to update the payment method");
  }
  // mandatory for the API. Rest are optional
  if (!body.display_string.trim()) {
    throw new Error(
      "Payment method display string is missing when attempting to update the payment method",
    );
  }

  const response = await paymentsProxy(ctx, `/v2/payment_method/${id}`, "PATCH", body);
  assertOk(response);
  return response.json();
};

export const updateBankAccount = async (ctx, bankAccountId, body) => {
  if (!bankAccountId) {
    throw new Error("Bank account ID is missing when attempting to update the bank account");
  }
  // mandatory for the API. Rest are optional
  if (!body.display_string.trim()) {
    throw new Error(
      "Bank account display string is missing when attempting to update the bank account",
    );
  }

  const response = await paymentsProxy(ctx, `/v1/banking/${bankAccountId}`, "PATCH", body);
  assertOk(response);
  return response.json();
};

export const deletePaymentMethod = async (ctx, id) => {
  if (!id) {
    throw new Error("Payment method ID is missing when attempting to delete the payment method");
  }
  const response = await paymentsProxy(ctx, `/v1/payment_method/${id}`, "DELETE");
  const data = await response.json();
  assertOk(response, [], data);
  return Promise.resolve(data);
};

export const deleteBankAccount = async (ctx, bankAccountId) => {
  if (!bankAccountId) {
    throw new Error("Bank account ID is missing when attempting to delete the bank account");
  }
  const response = await paymentsProxy(ctx, `/v1/banking/${bankAccountId}`, "DELETE");
  assertOk(response);
  return response.json();
};

export const getBankRailOptions = async (ctx, bankAccountId) => {
  if (!bankAccountId) {
    throw new Error("Bank account ID is missing when attempting to get bank rail options");
  }
  const response = await paymentsProxy(ctx, `/v1/payment_method/masspay`, "POST", {
    bank_account_id: bankAccountId,
  });

  assertOk(response);
  return response.json();
};

export const getBankRailOptionsV2 = async (ctx, args) => {
  const { bankAccountId, currency, requested_amount, bankRailsVendor } = typeof args === "object" ? args : {};
  if (!bankAccountId || !currency || !requested_amount) {
    throw new Error("Missing required information when attempting to get bank rail options");
  }
  const response = await paymentsProxy(
    ctx,
    `/credit/options/${bankRailsVendor || "masspay"}?bank_account_id=${bankAccountId}&currency=${currency}&requested_amount=${requested_amount}`,
    "GET",
  );

  const data = await response.json();
  assertOk(response, [], data);
  return Promise.resolve(data);
};

export const getIbanOptions = async (ctx) => {
  const response = await paymentsProxy(ctx, `/v1/banking/tilia`, "POST");

  assertOk(response);
  return response.json();
};

export const createIbanPaymentMethod = async (ctx, iban, countryISO) => {
  if (!iban) {
    throw new Error("IBAN is missing when attempting to create a banking payment method");
  } else if (!countryISO) {
    throw new Error("country ISO is missing when attempting to create a banking payment method");
  }
  const response = await paymentsProxy(ctx, `/v1/banking/tilia`, "PUT", {
    account_number: iban,
    institution_identifier: null,
    extra_identifier: null,
    extra_identifier_type: null,
    institution_name: null,
    country: countryISO,
    method: "IBAN",
    subtype: null,
  });
  assertOk(response);
  return response.json();
};

// currency, for now, will always be USD since plaid is only configured for USD
export const createBankingPaymentMethod = async (
  ctx,
  bankAccountId,
  destinationToken,
  currency = "USD",
) => {
  if (!bankAccountId) {
    throw new Error(
      "Bank account ID is missing when attempting to create a banking payment method",
    );
  } else if (!destinationToken) {
    throw new Error(
      "Destination token is missing when attempting to create a banking payment method",
    );
  }
  const response = await paymentsProxy(ctx, `/v1/payment_method/masspay`, "PUT", {
    bank_account_id: bankAccountId,
    destination_token: destinationToken,
    currency,
  });
  assertOk(response);
  return response.json();
};

export const startCreateTiliaBankAccount = async (
  ctx,
  countryCode,
  currency = "USD"
) => {
  if (!countryCode) {
    throw new Error("Missing countryCode when attempting to startCreateTiliaBankAccount");
  }

  const response = await paymentsProxy(ctx, "/v2/banking/tilia", "POST", {
    country: countryCode,
    currency,
  });
  const data = await response.json();
  assertOk(response);
  return Promise.resolve(data);
};

export const finishCreateTiliaBankAccount = async (ctx, formData) => {
  if (!formData) {
    throw new Error("Missing formData when attempting to finishCreateTiliaBankAccount");
  }
  const response = await paymentsProxy(
    ctx,
    "/v2/banking/tilia",
    "PUT",
    formData,
  );
  const data = await response.json();
  assertOk(response, [], data);
  return Promise.resolve(data);
};

export const updateTiliaBankAccountUserAddress = async (ctx, bankAccountOwnerId, formData) => {
  if (!bankAccountOwnerId || !formData) {
    throw new Error("Missing required data when attempting to updateTiliaBankAccountUserAddress");
  }
  const response = await paymentsProxy(
    ctx,
    `/v1/banking/owner/${bankAccountOwnerId}`,
    "PATCH",
    formData,
  );
  const data = await response.json();
  assertOk(response, [], data);
  return Promise.resolve(data);
};
